import React, { forwardRef } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Polyfill to prevent "date.toISOString is not a function" when legacy pages
// receive a formatted string from DatePicker instead of a Date object.
if (typeof String.prototype.toISOString !== "function") {
  String.prototype.toISOString = function () {
    return this.toString();
  };
}

/**
 * Reusable DatePicker Component — Ocean Blue Design System
 * 
 * Props:
 *  selected        : Date | string (current date value)
 *  onChange        : Function (callback on date change)
 *  placeholder     : string (placeholder text)
 *  minDate         : Date (minimum selectable date)
 *  maxDate         : Date (maximum selectable date)
 *  disabled        : boolean (disable the input)
 *  readOnly        : boolean (make the input read-only)
 *  label           : string (optional floating label)
 *  required        : boolean (show red asterisk on label)
 *  error           : string (error message)
 *  className       : string (custom styles for the input)
 *  wrapperClassName: string (custom styles for the outer wrapper)
 *  popperPlacement : string (placement of the calendar popup)
 */

const parseValueToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const str = String(value).trim();
  if (str.includes("-")) {
    const parts = str.split("-");
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      const date = new Date(str);
      return isNaN(date.getTime()) ? null : date;
    } else {
      // DD-MM-YYYY
      const [day, month, year] = parts;
      const date = new Date(`${year}-${month}-${day}`);
      return isNaN(date.getTime()) ? null : date;
    }
  } else if (str.includes(".")) {
    // DD.MM.YYYY
    const [day, month, year] = str.split(".");
    const date = new Date(`${year}-${month}-${day}`);
    return isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(str);
  return isNaN(date.getTime()) ? null : date;
};

const DatePicker = forwardRef(
  (
    {
      selected,
      onChange,
      placeholder,
      minDate,
      maxDate,
      disabled = false,
      readOnly = false,
      label,
      required = false,
      error,
      className = "",
      wrapperClassName = "",
      popperPlacement = "bottom-start",
      // "iso" (default) emits YYYY-MM-DD. "dot" emits DD.MM.YYYY — the only
      // format adminLoanController.parseEuropeanDate accepts, and the format
      // 745 of the 748 live proposed_loan_details.emiDate rows are stored in.
      // Opt-in, so fields like dob keep the ISO value they already save.
      valueFormat = "iso",
      ...props
    },
    ref
  ) => {
    const isDotFormat = valueFormat === "dot";

    // Base input styling matching Ocean Blue Design System (h-[38px] to align with standard inputs and prevent text cutting)
    const inputClass = [
      "form-input bg-white rounded-lg border w-full pl-3.5 pr-10 py-2 text-sm transition-all duration-150 focus:outline-none focus:ring-1 h-[38px] flex items-center",
      error
        ? "border-danger-500 focus:border-danger-500 focus:ring-danger-500/20 text-danger-900"
        : "border-border-medium hover:border-primary focus:border-primary focus:ring-primary/20 text-ink-700",
      disabled || readOnly
        ? "bg-[#F8FAFC] border-border text-ink-400 cursor-not-allowed"
        : "",
      className
    ]
      .filter(Boolean)
      .join(" ");

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 120 }, (_, i) => currentYear - 100 + i);
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div className={["relative w-full", wrapperClassName].filter(Boolean).join(" ")}>
        {label && (
          <label
            className={[
              "absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide transition-colors duration-150 select-none z-10 pointer-events-none",
              disabled || readOnly
                ? "text-ink-400"
                : error
                  ? "text-danger-500"
                  : "text-ink-400",
            ].join(" ")}
          >
            {label}
            {required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <ReactDatePicker
            selected={parseValueToDate(selected)}
            onChange={(date) => {
              if (onChange) {
                if (date) {
                  const day = String(date.getDate()).padStart(2, "0");
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const year = date.getFullYear();
                  onChange(
                    isDotFormat
                      ? `${day}.${month}.${year}`   // DD.MM.YYYY
                      : `${year}-${month}-${day}`,  // standard YYYY-MM-DD
                    date
                  );
                } else {
                  onChange("", null);
                }
              }
            }}
            onChangeRaw={(e) => {
              if (e && e.target && typeof e.target.value === "string") {
                // Keep the separator this field actually uses. Stripping "." in
                // dot mode would delete the dots as the user typed them, making
                // a DD.MM.YYYY field impossible to fill by hand.
                const clean = e.target.value.replace(
                  isDotFormat ? /[^0-9.]/g : /[^0-9-]/g,
                  ""
                );
                if (e.target.value !== clean) {
                  e.target.value = clean;
                }
              }
            }}
            // Display matches the stored value, so the box never shows one
            // format while the form holds another.
            dateFormat={isDotFormat ? "dd.MM.yyyy" : "dd-MM-yyyy"}
            minDate={minDate}
            maxDate={maxDate}
            className={inputClass}
            wrapperClassName="w-full"
            placeholderText={
              placeholder ?? (isDotFormat ? "DD.MM.YYYY" : "DD-MM-YYYY")
            }
            disabled={disabled || readOnly}
            readOnly={readOnly}
            popperPlacement={popperPlacement}
            fixedHeight
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50 border-b border-slate-100 rounded-t-xl">
                {/* Prev Month Button */}
                <button
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  type="button"
                  className="p-1 rounded hover:bg-white border border-transparent hover:border-slate-200 text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent transition-all duration-150"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                </button>

                {/* Dropdowns */}
                <div className="flex items-center gap-1">
                  <select
                    value={months[date.getMonth()]}
                    onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                    className="px-1 py-0.5 text-[10px] font-bold rounded bg-white border border-slate-200 text-slate-800 focus:outline-none cursor-pointer hover:border-slate-300 max-w-[76px] h-5.5"
                  >
                    {months.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <select
                    value={date.getFullYear()}
                    onChange={({ target: { value } }) => changeYear(parseInt(value))}
                    className="px-1 py-0.5 text-[10px] font-bold rounded bg-white border border-slate-200 text-slate-800 focus:outline-none cursor-pointer hover:border-slate-300 max-w-[54px] h-5.5"
                  >
                    {years.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Next Month Button */}
                <button
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                  type="button"
                  className="p-1 rounded hover:bg-white border border-transparent hover:border-slate-200 text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent transition-all duration-150"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </div>
            )}
            {...props}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
          </div>
        </div>

        {error && <p className="text-xs text-danger-500 mt-1 font-medium">{error}</p>}
      </div>
    );
  }
);

DatePicker.displayName = "DatePicker";

export default DatePicker;
